import React, {useMemo} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    buildCollectionCoverageView,
    collectionTabCounts,
} from '../buildCollectionCoverageView';
import {COLLECTION_TABS} from '../coverageModalUrlState';
import {openReportExcerptInNewTab} from '../navigateToReportExcerpt';
import TabLabelWithCount from '../components/TabLabelWithCount';
import CollectionCoverageOverviewTab from './CollectionCoverageOverviewTab';
import CollectionCoverageEeisTab from './CollectionCoverageEeisTab';
import CollectionCitationsTab from './CollectionCitationsTab';

/**
 * Collection Coverage modal — Overview / EEI / Citations. All open/tab/expanded
 * state is URL-driven (P4 scheme: ?coverage=collection&tab=…&eei=…) and wired
 * by the parent, so C2 returnTo round-trips restore the exact state.
 */
const CollectionCoverageModal = ({
    open,
    onClose,
    snapshot,
    icrNumber,
    subject,
    pirEeis = null,
    tab = COLLECTION_TABS.OVERVIEW,
    expandedEeiId = null,
    onTabChange,
    onToggleEei,
    onOpenMaterial,
    onRecompute,
    recomputing = false,
    canRecompute = false,
}) => {
    const view = useMemo(() => buildCollectionCoverageView(snapshot), [snapshot]);
    const counts = useMemo(() => collectionTabCounts(view, pirEeis), [view, pirEeis]);
    const showRecompute = Boolean(canRecompute && onRecompute);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{pr: 6}}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={700}>
                        Data Coverage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {icrNumber}{subject ? ` · ${subject}` : ''}
                    </Typography>
                </Stack>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{position: 'absolute', right: 8, top: 8}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <Tabs
                value={tab}
                onChange={(_, next) => onTabChange?.(next)}
                sx={{px: 2, borderBottom: 1, borderColor: 'divider'}}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab value={COLLECTION_TABS.OVERVIEW} label="Overview"/>
                <Tab
                    value={COLLECTION_TABS.EEI}
                    label={(
                        <TabLabelWithCount
                            label="EEI"
                            count={counts.eeis}
                            selected={tab === COLLECTION_TABS.EEI}
                        />
                    )}
                />
                <Tab
                    value={COLLECTION_TABS.CITATIONS}
                    label={(
                        <TabLabelWithCount
                            label="Citations"
                            count={counts.citations}
                            selected={tab === COLLECTION_TABS.CITATIONS}
                        />
                    )}
                />
            </Tabs>

            <DialogContent dividers>
                {tab === COLLECTION_TABS.OVERVIEW && (
                    <CollectionCoverageOverviewTab
                        view={view}
                        pirEeis={pirEeis}
                        computing={Boolean(recomputing || view?.computing)}
                    />
                )}
                {tab === COLLECTION_TABS.EEI && (
                    <CollectionCoverageEeisTab
                        view={view}
                        pirEeis={pirEeis}
                        expandedEeiId={expandedEeiId}
                        onToggleEei={onToggleEei}
                        onOpenMaterial={onOpenMaterial}
                    />
                )}
                {tab === COLLECTION_TABS.CITATIONS && (
                    <CollectionCitationsTab
                        view={view}
                        onOpenMaterial={onOpenMaterial}
                        onOpenReport={openReportExcerptInNewTab}
                    />
                )}
            </DialogContent>

            {showRecompute && (
                <DialogActions sx={{px: 2, py: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1}}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={recomputing
                            ? <CircularProgress size={16} color="inherit"/>
                            : <RefreshIcon/>}
                        disabled={recomputing}
                        aria-busy={recomputing}
                        onClick={() => {
                            if (!recomputing) {
                                onRecompute?.();
                            }
                        }}
                    >
                        {recomputing
                            ? ([
                                snapshot?.collectionCoverage?.progressLabel,
                                snapshot?.collectionCoverage?.progressDetail,
                            ].filter(Boolean).join(' — ') || 'Recomputing…')
                            : 'Recompute'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default CollectionCoverageModal;
